import { useEffect, useMemo, useRef, useState, memo, useCallback } from "react";
import {
  Loader2,
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
  RotateCcw,
  Settings,
} from "lucide-react";
import Hls from "hls.js";

/* ───────── helpers ───────── */
const formatTime = (secs) => {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const normalizeBunnySource = (rawSrc) => {
  if (rawSrc === undefined || rawSrc === null) return { mode: "none", url: "" };
  const str = String(rawSrc).trim();
  if (!str) return { mode: "none", url: "" };

  const iframeMatch = str.match(/<iframe[^>]*src=["']([^"']+)["']/i);
  if (iframeMatch?.[1]) return { mode: "iframe", url: iframeMatch[1] };

  const lower = str.toLowerCase();
  if (
    lower.includes("/embed/") ||
    lower.includes("bunnycdn") ||
    lower.includes("mediadelivery")
  )
    return { mode: "iframe", url: str };

  if (lower.includes(".m3u8") || lower.includes("playlist.m3u8"))
    return { mode: "hls", url: str };

  if (/\.(mp4|webm|mov|mkv)(\?|$)/i.test(lower))
    return { mode: "file", url: str };

  return { mode: "file", url: str };
};

/* ───────── component ───────── */
function BunnyPlayer({ src, title, userEmail }) {
  /* refs */
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsTimer = useRef(null);
  const settingsRef = useRef(null);

  /* state */
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [levels, setLevels] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState("auto");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [emailPosition, setEmailPosition] = useState({
    top: "15%",
    left: "15%",
    opacity: 0,
  });

  /* derived */
  const source = useMemo(() => normalizeBunnySource(src), [src]);
  const videoUrl =
    source.mode === "hls" || source.mode === "file" ? source.url : "";
  const iframeUrl = source.mode === "iframe" ? source.url : "";
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const currentLabel = formatTime(currentTime);
  const durationLabel = formatTime(duration);

  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
      (window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1)
    );
  }, []);

  const isFull = isFullscreen || isPseudoFullscreen;

  /* ───────── reset ───────── */
  const resetUiState = useCallback(() => {
    setStatus(
      videoUrl ? "loading" : source.mode === "iframe" ? "ready" : "idle"
    );
    setErrorMessage("");
    setIsPlaying(false);
    setIsMuted(false);
    setVolume(1);
    setDuration(0);
    setCurrentTime(0);
    setIsBuffering(false);
    setLevels([]);
    setSelectedQuality("auto");
    setPlaybackRate(1);
    setAutoRetryCount(0);
    setIsPseudoFullscreen(false);
    setIsFullscreen(false);
    setEmailPosition((p) => ({ ...p, opacity: 1 }));
  }, [videoUrl, source.mode]);

  useEffect(() => {
    resetUiState();
  }, [resetUiState]);

  /* ───────── playback rate ───────── */
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = playbackRate;
  }, [playbackRate]);

  /* ───────── attach source ───────── */
  useEffect(() => {
    if (source.mode === "iframe" || source.mode === "none" || !videoUrl) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setStatus("loading");
    setErrorMessage("");
    setIsBuffering(false);

    video.controls = false;
    video.loop = false;
    video.preload = "auto";
    video.muted = isMuted;
    video.volume = volume;
    video.playbackRate = playbackRate;

    const handleLoadedMetadata = () => {
      setDuration(video.duration || 0);
      setStatus("ready");
      setIsBuffering(false);
      if (autoRetryCount !== 0) setAutoRetryCount(0);
    };
    const handlePlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      startHideControlsTimer();
    };
    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true);
      clearHideControlsTimer();
    };
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.duration) setDuration(video.duration);
    };
    const handleWaiting = () => {
      if (status === "ready") setIsBuffering(true);
    };
    const handleCanPlay = () => setIsBuffering(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };
    const handleError = () => {
      setIsPlaying(false);
      setIsBuffering(false);
      if (autoRetryCount < 2) {
        setAutoRetryCount((p) => p + 1);
        setReloadKey((p) => p + 1);
        return;
      }
      setStatus("error");
      setErrorMessage("Video failed to load. Please try again.");
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (source.mode === "hls" && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const mapped = (hls.levels || []).map((lvl, i) => {
          const h = lvl.height || lvl.attrs?.RESOLUTION?.split("x")[1];
          const b = lvl.bitrate || lvl.attrs?.BANDWIDTH;
          return {
            index: i,
            label: `${h || "L" + i}${b ? ` • ${(b / 1e6).toFixed(1)} Mbps` : ""}`,
          };
        });
        setLevels(mapped);
        setSelectedQuality("auto");
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal) {
          setStatus("error");
          setErrorMessage("Network error while loading video.");
          hls.destroy();
        }
      });
      hlsRef.current = hls;
    } else if (
      source.mode === "hls" &&
      video.canPlayType("application/vnd.apple.mpegurl")
    ) {
      video.src = videoUrl;
    } else {
      video.src = videoUrl;
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, source.mode, reloadKey]);

  /* ───────── fullscreen change listeners (browser) ───────── */
  useEffect(() => {
    const handler = () => {
      const el =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(Boolean(el));
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    document.addEventListener("mozfullscreenchange", handler);
    document.addEventListener("MSFullscreenChange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
      document.removeEventListener("mozfullscreenchange", handler);
      document.removeEventListener("MSFullscreenChange", handler);
    };
  }, []);

  /* ───────── iOS native video fullscreen listeners ───────── */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onBegin = () => setIsFullscreen(true);
    const onEnd = () => setIsFullscreen(false);
    video.addEventListener("webkitbeginfullscreen", onBegin);
    video.addEventListener("webkitendfullscreen", onEnd);
    return () => {
      video.removeEventListener("webkitbeginfullscreen", onBegin);
      video.removeEventListener("webkitendfullscreen", onEnd);
    };
  }, [reloadKey, source.mode, videoUrl]);

  /* ───────── pseudo-fullscreen body lock ───────── */
  useEffect(() => {
    if (!isPseudoFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, [isPseudoFullscreen]);

  /* ───────── close settings on outside click ───────── */
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target))
        setShowSettings(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showSettings]);

  /* ───────── keyboard shortcuts ───────── */
  useEffect(() => {
    if (!videoUrl) return;
    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      switch (e.key.toLowerCase()) {
        case " ":
        case "k":
          e.preventDefault();
          togglePlay(e);
          break;
        case "m":
          toggleMute(e);
          break;
        case "f":
          toggleFullscreen(e);
          break;
        case "arrowleft":
          e.preventDefault();
          seekRelative(-5);
          break;
        case "arrowright":
          e.preventDefault();
          seekRelative(5);
          break;
        case "arrowup":
          e.preventDefault();
          changeVolume(Math.min(1, volume + 0.1));
          break;
        case "arrowdown":
          e.preventDefault();
          changeVolume(Math.max(0, volume - 0.1));
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, volume]);

  /* ───────── email watermark position ───────── */
  useEffect(() => {
    if (!userEmail) return;
    const interval = setInterval(() => {
      setEmailPosition({
        top: `${Math.floor(Math.random() * 70) + 10}%`,
        left: `${Math.floor(Math.random() * 70) + 10}%`,
        opacity: 0.9,
      });
      setTimeout(() => {
        setEmailPosition((p) => ({ ...p, opacity: 1 }));
      }, 2200);
    }, 6500);
    return () => clearInterval(interval);
  }, [userEmail]);

  /* ───────── cleanup timer on unmount ───────── */
  useEffect(() => {
    return () => clearHideControlsTimer();
  }, []);

  /* ───────── control helpers ───────── */
  const clearHideControlsTimer = () => {
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
      hideControlsTimer.current = null;
    }
  };

  const startHideControlsTimer = () => {
    clearHideControlsTimer();
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2800);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (isPlaying) startHideControlsTimer();
  };

  /* ───────── actions ───────── */
  const togglePlay = (e) => {
    if (e) e.stopPropagation();
    const video = videoRef.current;
    if (!video || source.mode === "iframe") return;
    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          startHideControlsTimer();
        })
        .catch(() => {
          setStatus("error");
          setErrorMessage("Unable to start playback.");
        });
    } else {
      video.pause();
    }
  };

  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (!video.muted && video.volume === 0) {
      video.volume = 0.4;
      setVolume(0.4);
    }
  };

  const changeVolume = (value) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(1, Math.max(0, value));
    video.volume = next;
    video.muted = next === 0;
    setVolume(next);
    setIsMuted(video.muted);
  };

  const handleSeek = (value) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const pct = Math.min(100, Math.max(0, Number(value)));
    video.currentTime = (pct / 100) * duration;
    setCurrentTime(video.currentTime);
  };

  const seekRelative = (delta) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(
      Math.max(video.currentTime + delta, 0),
      video.duration || video.currentTime
    );
    video.currentTime = next;
    setCurrentTime(next);
  };

  /* ── fullscreen with iOS support ── */
  const toggleFullscreen = async (e) => {
    if (e) e.stopPropagation();

    const target = containerRef.current;
    const video = videoRef.current;
    if (!target && !video) return;

    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    /* ── iOS ── */
    if (isIOS && video) {
      try {
        if (isPseudoFullscreen) {
          setIsPseudoFullscreen(false);
          setIsFullscreen(false);
          return;
        }
        if (typeof video.webkitEnterFullscreen === "function") {
          video.webkitEnterFullscreen();
          return;
        }
        if (typeof video.webkitRequestFullscreen === "function") {
          await video.webkitRequestFullscreen();
          return;
        }
        if (typeof video.requestFullscreen === "function") {
          await video.requestFullscreen();
          return;
        }
        setIsPseudoFullscreen(true);
        setIsFullscreen(true);
        return;
      } catch {
        setIsPseudoFullscreen(true);
        setIsFullscreen(true);
        return;
      }
    }

    /* ── Standard browsers ── */
    try {
      if (!fsElement) {
        if (target?.requestFullscreen) await target.requestFullscreen();
        else if (target?.webkitRequestFullscreen)
          await target.webkitRequestFullscreen();
        else if (target?.mozRequestFullScreen)
          await target.mozRequestFullScreen();
        else if (target?.msRequestFullscreen)
          await target.msRequestFullscreen();
        else {
          setIsPseudoFullscreen(true);
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen)
          await document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen)
          await document.mozCancelFullScreen();
        else if (document.msExitFullscreen) await document.msExitFullscreen();
        else {
          setIsPseudoFullscreen(false);
          setIsFullscreen(false);
        }
      }
    } catch {
      setIsPseudoFullscreen((p) => !p);
      setIsFullscreen((p) => !p);
    }
  };

  const handleRetry = (e) => {
    if (e) e.stopPropagation();
    setAutoRetryCount(0);
    setReloadKey((p) => p + 1);
    setStatus("loading");
  };

  const handleQualityChange = (e) => {
    const v = e.target.value;
    setSelectedQuality(v);
    const hls = hlsRef.current;
    if (!hls) return;
    if (v === "auto") {
      hls.currentLevel = -1;
      return;
    }
    const parsed = parseInt(v, 10);
    if (!Number.isNaN(parsed)) hls.currentLevel = parsed;
  };

  const handleSpeedChange = (e) => {
    const v = parseFloat(e.target.value);
    if (!Number.isNaN(v)) setPlaybackRate(v);
  };

  /* ───────── theme tokens ───────── */
  const accent = "#27be9f";
  const accentDark = "#1faa8d";
  const accentSoft = "rgba(39,190,159,0.16)";
  const borderSoft = "1px solid rgba(39,190,159,0.18)";
  const surface = "rgba(6, 14, 13, 0.78)";

  /* ───────── render: nothing ───────── */
  if (!videoUrl && !iframeUrl) {
    return (
      <div
        style={{
          width: "100%",
          padding: "48px 24px",
          background:
            "linear-gradient(180deg,rgba(8,18,17,1) 0%,rgba(2,7,6,1) 100%)",
          color: "white",
          borderRadius: "24px",
          border: borderSoft,
          textAlign: "center",
          boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
        }}
      >
        No video source available.
      </div>
    );
  }

  /* ───────── common styles ───────── */
  const containerStyle = {
    width: isPseudoFullscreen ? "100vw" : "100%",
    height: isPseudoFullscreen ? "100dvh" : "auto",
    position: isPseudoFullscreen ? "fixed" : "relative",
    inset: isPseudoFullscreen ? 0 : "auto",
    zIndex: isPseudoFullscreen ? 999999 : "auto",
    borderRadius: isFull ? "0px" : "24px",
    background:
      "radial-gradient(circle at top right,rgba(39,190,159,.18),transparent 28%),linear-gradient(180deg,#081211 0%,#000 100%)",
    aspectRatio: isFull ? "auto" : "16 / 9",
    overflow: "hidden",
    boxShadow: isFull
      ? "none"
      : "0 30px 70px rgba(0,0,0,0.30),0 0 0 1px rgba(39,190,159,0.06)",
    border: isFull ? "none" : borderSoft,
    minHeight: isFull ? "100dvh" : "0",
  };

  const iconBtn = {
    width: 38,
    height: 38,
    borderRadius: "999px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    cursor: "pointer",
    flexShrink: 0,
    backdropFilter: "blur(10px)",
    transition: "all 0.25s ease",
  };

  const miniBtn = {
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    color: "white",
    fontSize: "11px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    backdropFilter: "blur(10px)",
  };

  const selStyle = {
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: "10px",
    padding: "6px 8px",
    color: "white",
    fontSize: "12px",
    outline: "none",
    cursor: "pointer",
  };

  const popupOpt = (active) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: "12px",
    border: active
      ? "1px solid rgba(95,227,202,0.45)"
      : "1px solid rgba(255,255,255,0.08)",
    background: active ? accentSoft : "rgba(255,255,255,0.04)",
    color: "white",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "left",
  });

  /* ── title bar ── */
  const TitleBar = () =>
    title ? (
      <div
        style={{
          position: "absolute",
          top: 18,
          left: 18,
          right: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          zIndex: 6,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            borderRadius: "999px",
            background: accent,
            color: "white",
            fontWeight: 800,
            boxShadow: "0 12px 28px rgba(39,190,159,0.35)",
          }}
        >
          <span style={{ fontSize: 13 }}>{title}</span>
          <span
            style={{
              fontSize: 9,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              padding: "4px 8px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.24)",
            }}
          >
            Secure Player
          </span>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            borderRadius: 14,
            background: surface,
            color: "rgba(255,255,255,0.88)",
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.16em",
            border: borderSoft,
            backdropFilter: "blur(16px)",
          }}
        >
          <span>HD</span>
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: accent,
              boxShadow: "0 0 12px rgba(39,190,159,0.7)",
            }}
          />
          <span>{levels.length ? "Adaptive" : "Standard"}</span>
        </div>
      </div>
    ) : null;

  /* ───────── render: iframe mode ───────── */
  if (iframeUrl) {
    return (
      <div className="custom-bunny-player" style={{ width: "100%" }}>
        <div ref={containerRef} style={containerStyle}>
          <TitleBar />
          <iframe
            title={title || "Course video"}
            src={iframeUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              background: "black",
            }}
          />
        </div>
      </div>
    );
  }

  /* ───────── render: native player ───────── */
  return (
    <div className="custom-bunny-player" style={{ width: "100%" }}>
      <div
        ref={containerRef}
        style={containerStyle}
        className="custom-bunny-player-container"
        onMouseMove={handleMouseMove}
        onClick={togglePlay}
      >
        <TitleBar />

        {/* ── video element ── */}
        <video
          ref={videoRef}
          playsInline
          controls={false}
          webkit-playsinline="true"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            background: "black",
          }}
          onClick={(e) => e.stopPropagation()}
        />

        {/* ── loading spinner ── */}
        {(status === "loading" || isBuffering) && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(6px)",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgba(6,14,13,0.75)",
                border: borderSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 20px 35px rgba(0,0,0,0.28)",
              }}
            >
              <Loader2
                className="animate-spin"
                style={{ width: 30, height: 30, color: accent }}
              />
            </div>
          </div>
        )}

        {/* ── error overlay ── */}
        {status === "error" && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center"
            style={{
              background: "rgba(0,0,0,0.72)",
              color: "white",
              backdropFilter: "blur(8px)",
            }}
          >
            <div
              style={{
                padding: "18px 22px",
                borderRadius: 20,
                background: "rgba(6,14,13,0.75)",
                border: borderSoft,
                maxWidth: 360,
              }}
            >
              <p style={{ margin: 0, marginBottom: 14 }}>{errorMessage}</p>
              <button
                onClick={handleRetry}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                  borderRadius: "999px",
                  background: accent,
                  border: "none",
                  color: "white",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 10px 24px rgba(39,190,159,0.30)",
                }}
              >
                <RotateCcw size={16} />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* ── big play button ── */}
        {!isPlaying && status === "ready" && !isBuffering && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button
              onClick={togglePlay}
              className="pointer-events-auto"
              style={{
                width: 78,
                height: 78,
                borderRadius: "50%",
                background: `linear-gradient(135deg,${accent} 0%,${accentDark} 100%)`,
                color: "white",
                border: "1px solid rgba(255,255,255,0.22)",
                boxShadow: "0 20px 40px rgba(39,190,159,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Play size={30} fill="currentColor" style={{ marginLeft: 3 }} />
            </button>
          </div>
        )}

        {/* ── email watermark ── */}
        {userEmail && (
          <div
            style={{
              position: "absolute",
              top: emailPosition.top,
              left: emailPosition.left,
              transform: "translate(-50%,-50%)",
              padding: "7px 14px",
              borderRadius: "999px",
              background: "rgba(0,0,0,0.52)",
              color: "white",
              fontSize: 12,
              letterSpacing: "0.03em",
              pointerEvents: "none",
              opacity: emailPosition.opacity,
              transition: "opacity 0.4s ease",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(39,190,159,0.16)",
              zIndex: 6,
            }}
          >
            {userEmail}
          </div>
        )}

        {/* ══════════════ CONTROLS BAR ══════════════ */}
        <div
          className={`absolute inset-x-0 bottom-0 transition-all duration-300 ${
            showControls
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6 pointer-events-none"
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ padding: isFull ? "22px 24px 24px" : "16px" }}
        >
          {/* gradient behind controls */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg,rgba(0,0,0,0) 0%,rgba(0,0,0,0.18) 20%,rgba(0,0,0,0.88) 100%)",
              backdropFilter: "blur(8px)",
              pointerEvents: "none",
            }}
          />

          {/* ── settings popup (mobile) ── */}
          {showSettings && (
            <div
              ref={settingsRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                bottom: isFull ? 92 : 78,
                right: 16,
                zIndex: 50,
                minWidth: 230,
                maxWidth: 260,
                maxHeight: 350,
                overflowY: "auto",
                padding: 18,
                borderRadius: 22,
                background: "rgba(6,14,13,0.95)",
                border: borderSoft,
                backdropFilter: "blur(18px)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
              }}
            >
              {/* header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                  paddingBottom: 10,
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontWeight: 900,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.2em",
                  }}
                >
                  Video Settings
                </span>
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: accent,
                    boxShadow: "0 0 12px rgba(39,190,159,0.65)",
                  }}
                />
              </div>

              {/* speed */}
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    color: "rgba(255,255,255,0.56)",
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    marginBottom: 10,
                  }}
                >
                  Playback Speed
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                    gap: 8,
                  }}
                >
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                    <button
                      key={s}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPlaybackRate(s);
                      }}
                      style={{
                        ...popupOpt(playbackRate === s),
                        textAlign: "center",
                        padding: "10px 6px",
                      }}
                    >
                      {s === 1 ? "Normal" : `${s}x`}
                    </button>
                  ))}
                </div>
              </div>

              {/* quality */}
              {levels.length > 0 && (
                <div>
                  <div
                    style={{
                      color: "rgba(255,255,255,0.56)",
                      fontWeight: 700,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.16em",
                      marginBottom: 10,
                    }}
                  >
                    Resolution
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {[
                      { index: "auto", label: "Auto Detection" },
                      ...levels.map((l) => ({
                        index: l.index,
                        label: l.label,
                      })),
                    ].map((q) => (
                      <button
                        key={q.index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQualityChange({
                            target: { value: String(q.index) },
                          });
                        }}
                        style={popupOpt(
                          String(selectedQuality) === String(q.index)
                        )}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── control rows container ── */}
          <div
            className="custom-players-web-controllers"
            style={{
              position: "relative",
              zIndex: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* ROW 1: progress */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
              }}
            >
              <span
                style={{
                  color: "white",
                  fontFamily: "monospace",
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  background: "rgba(39,190,159,0.18)",
                  border: "1px solid rgba(39,190,159,0.18)",
                  padding: "4px 10px",
                  borderRadius: "999px",
                }}
              >
                {currentLabel}
              </span>

              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={progress}
                onChange={(e) => handleSeek(e.target.value)}
                className="custom-slider"
                style={{ flex: 1, minWidth: 0 }}
              />

              <span
                style={{
                  color: "rgba(255,255,255,0.72)",
                  fontFamily: "monospace",
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {durationLabel}
              </span>
            </div>

            {/* ROW 2: buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {/* left group */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    seekRelative(-10);
                  }}
                  className="hidden sm:block"
                  style={miniBtn}
                >
                  -10s
                </button>

                <button
                  onClick={togglePlay}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: `linear-gradient(135deg,${accent} 0%,${accentDark} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.18)",
                    cursor: "pointer",
                    boxShadow: "0 10px 24px rgba(39,190,159,0.32)",
                  }}
                >
                  {isPlaying ? (
                    <Pause size={18} />
                  ) : (
                    <Play
                      size={18}
                      fill="currentColor"
                      style={{ marginLeft: 2 }}
                    />
                  )}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    seekRelative(10);
                  }}
                  className="hidden sm:block"
                  style={miniBtn}
                >
                  +10s
                </button>

                {/* mobile seek */}
                <div className="flex sm:hidden" style={{ gap: 4 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      seekRelative(-10);
                    }}
                    style={{ ...miniBtn, padding: "5px 8px", fontSize: 10 }}
                  >
                    -10s
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      seekRelative(10);
                    }}
                    style={{ ...miniBtn, padding: "5px 8px", fontSize: 10 }}
                  >
                    +10s
                  </button>
                </div>
              </div>

              {/* right group */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {/* mute */}
                <button onClick={toggleMute} style={iconBtn}>
                  {isMuted || volume === 0 ? (
                    <VolumeX size={16} />
                  ) : (
                    <Volume2 size={16} />
                  )}
                </button>

                {/* volume slider - desktop */}
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  className="custom-slider hidden sm:block"
                  style={{ width: 72 }}
                />

                {/* desktop: quality + speed */}
                <div
                  className="hidden sm:flex"
                  style={{
                    alignItems: "center",
                    gap: 8,
                    background: surface,
                    padding: "6px 10px",
                    borderRadius: "999px",
                    border: borderSoft,
                    backdropFilter: "blur(12px)",
                  }}
                >
                  {levels.length > 0 && (
                    <>
                      <span
                        style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}
                      >
                        Q
                      </span>
                      <select
                        value={selectedQuality}
                        onChange={handleQualityChange}
                        onClick={(e) => e.stopPropagation()}
                        style={selStyle}
                      >
                        <option value="auto">Auto</option>
                        {levels.map((l) => (
                          <option key={l.index} value={l.index}>
                            {l.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <span
                    style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}
                  >
                    ×
                  </span>
                  <select
                    value={playbackRate}
                    onChange={handleSpeedChange}
                    onClick={(e) => e.stopPropagation()}
                    style={selStyle}
                  >
                    <option value={0.5}>0.5×</option>
                    <option value={0.75}>0.75×</option>
                    <option value={1}>1×</option>
                    <option value={1.25}>1.25×</option>
                    <option value={1.5}>1.5×</option>
                    <option value={2}>2×</option>
                  </select>
                </div>

                {/* mobile: settings button */}
                <button
                  className="flex sm:hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings((v) => !v);
                  }}
                  style={{
                    ...iconBtn,
                    background: showSettings ? accentSoft : iconBtn.background,
                    border: showSettings
                      ? "1px solid rgba(95,227,202,0.45)"
                      : iconBtn.border,
                  }}
                >
                  <Settings size={16} />
                </button>

                {/* fullscreen */}
                <button onClick={toggleFullscreen} style={iconBtn}>
                  {isFull ? <Minimize size={16} /> : <Maximize size={16} />}
                </button>
              </div>
            </div>
          </div>

          {/* ── slider css ── */}
          <style>{`
            .custom-slider {
              appearance: none;
              -webkit-appearance: none;
              height: 4px;
              background: linear-gradient(90deg,#27be9f 0%,#5ed8c0 100%);
              border-radius: 999px;
              outline: none;
              cursor: pointer;
            }
            .custom-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: white;
              border: 2px solid rgba(39,190,159,0.95);
              box-shadow: 0 2px 8px rgba(39,190,159,0.35);
              cursor: pointer;
            }
            .custom-slider::-moz-range-thumb {
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: white;
              border: 2px solid rgba(39,190,159,0.95);
              cursor: pointer;
            }
            .custom-bunny-player select option {
              background: #07100f;
              color: white;
            }
            @media (max-width: 640px) {
              .custom-slider { height: 3px; }
              .custom-slider::-webkit-slider-thumb { width: 12px; height: 12px; }
              .custom-slider::-moz-range-thumb { width: 12px; height: 12px; }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
}

export default memo(BunnyPlayer);
